import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { hashKey } from '@tanstack/query-core'
import { toValue } from 'vue'
import queryClient from './query_client.js'
import { ReApi, ReApiVersion, ReEndpoint } from './query_utils'

interface RegisteredV3AssessmentQuery {
  assessmentId: MaybeRefOrGetter<string | undefined>
  queryKey: ComputedRef<unknown[]>
}

interface RegisteredAssessmentIdentity {
  assessmentId: MaybeRefOrGetter<string | undefined>
  ref: MaybeRefOrGetter<string | undefined>
}

const assessmentIdentities = new Set<RegisteredAssessmentIdentity>()
const v3AssessmentQueries = new Set<RegisteredV3AssessmentQuery>()

export function invalidateV2Assessment(ref: string) {
  return queryClient.invalidateQueries({
    queryKey: [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.REQUEST, ref],
    exact: true,
  })
}

export function invalidateV2AssessmentById(assessmentId: string) {
  const refs = [...assessmentIdentities]
    .filter(identity => toValue(identity.assessmentId) === assessmentId)
    .map(identity => toValue(identity.ref))
    .filter(ref => ref !== undefined)

  return Promise.all([...new Set(refs)].map(invalidateV2Assessment))
}

export function invalidateUserRequestList() {
  return queryClient.invalidateQueries({
    queryKey: [ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.USER, 'request'],
  })
}

export function registerAssessmentIdentity(
  ref: MaybeRefOrGetter<string | undefined>,
  assessmentId: MaybeRefOrGetter<string | undefined>,
) {
  const registration = { ref, assessmentId }
  assessmentIdentities.add(registration)
  return () => assessmentIdentities.delete(registration)
}

export function registerV3ExpertAssessmentQuery(
  assessmentId: MaybeRefOrGetter<string | undefined>,
  queryKey: ComputedRef<unknown[]>,
) {
  const registration = { assessmentId, queryKey }
  v3AssessmentQueries.add(registration)
  return () => v3AssessmentQueries.delete(registration)
}

export function invalidateV3ExpertAssessment(ref: string) {
  const assessmentIds = new Set(
    [...assessmentIdentities]
      .filter(identity => toValue(identity.ref) === ref)
      .map(identity => toValue(identity.assessmentId))
      .filter(id => id !== undefined),
  )

  const queryKeys = new Map<string, unknown[]>()
  for (const query of v3AssessmentQueries) {
    const assessmentId = toValue(query.assessmentId)
    if (assessmentId !== undefined && assessmentIds.has(assessmentId))
      queryKeys.set(hashKey(query.queryKey.value), query.queryKey.value)
  }

  return Promise.all(
    [...queryKeys.values()]
      .map(queryKey => queryClient.invalidateQueries({
        queryKey,
        exact: true,
      })),
  )
}
